import { useEffect, useMemo, useRef, useState } from "react"

import { last } from "shared/lib/arrayUtils"
import {
  A_KEY,
  END_KEY,
  HOME_KEY,
  PAGE_DOWN_KEY,
  PAGE_UP_KEY,
  SPACE_KEY,
  isCommandKeyPress,
  isShiftKeyPress,
  isUnmodifiedKeyPress
} from "shared/lib/keys"
import { noop } from "shared/lib/noop"
import { useEvent } from "shared/lib/useEvent"
import { useFocus } from "shared/lib/useFocus"
import { useFocusWithin } from "shared/lib/useFocusWithin"
import { ArrowKeys } from "shared/types/ArrowKeys"
import { WithID } from "shared/types/WithID"

import { FlattenedTableItemType, OnTableItemSelect, TableCell } from "../types"

interface UseTableKeyboardControlsProps<T extends WithID> {
  items: FlattenedTableItemType<T>[]
  topOffset: number
  bottomOffset: number
  isItemCollapsed?: (item: T) => boolean
  containerRef: React.RefObject<HTMLDivElement | null>
  getRowCells?: (item: T) => TableCell[]
  onItemCollapse?: (id: string) => void
  onFocusTable: () => void
  scrollToIndex: (index: number) => void
  selectItem?: OnTableItemSelect
  selectItems?: (ids: string[]) => void
  rowFocusOnly?: boolean
}

export function useTableKeyboardControls<T extends WithID>({
  items,
  isItemCollapsed,
  containerRef,
  getRowCells,
  onItemCollapse,
  onFocusTable,
  scrollToIndex,
  selectItem,
  selectItems,
  rowFocusOnly
}: UseTableKeyboardControlsProps<T>) {
  const focusableRowIndex = useRef<number>(0)

  const [focusedID, setFocusedID] = useState<string | null>(null)
  // -1 is used to indicate that the row itself is focused
  const [focusedColIndex, setFocusedColIndex] = useState<number>(-1)

  const [focusableElement, setFocusableElement] =
    useState<null | HTMLDivElement>(null)

  const focusedRow = useMemo(() => {
    const index = items.findIndex(item => item.id === focusedID)
    if (index !== -1) {
      focusableRowIndex.current = index
      return index
    }
    if (items.length > 0) {
      const elementAtFocusedIndex = items[focusableRowIndex.current]
      if (elementAtFocusedIndex) {
        return focusableRowIndex.current
      }
      return items.length - 1
    }
    return 0
  }, [items, focusedID])

  const isTableFocused =
    useFocusWithin(containerRef.current) ||
    document.activeElement === containerRef.current

  const handleRowFocus = useEvent((index: number, withScrolling?: boolean) => {
    setFocusedID(items[index].id)
    if (withScrolling) {
      scrollToIndex(index)
    }
  })

  const handleFocus = useEvent((ev: FocusEvent) => {
    if (ev.relatedTarget) {
      scrollToIndex(focusedRow)
    }
  })

  useFocus({ element: containerRef.current, onFocus: handleFocus })

  // make first element focusable if no focusedID is set
  useEffect(() => {
    if (!focusedID && items[0]) {
      setFocusedID(items[0].id)
    }
  }, [items])

  const focusPreviousRow = useEvent((withScrolling?: boolean) => {
    const nextIndex = focusedRow > 0 ? focusedRow - 1 : 0
    handleRowFocus(nextIndex, withScrolling)
  })

  const focusNextRow = useEvent((withScrolling?: boolean) => {
    const maxIndex = items.length - 1
    const nextIndex = focusedRow < maxIndex ? focusedRow + 1 : items.length - 1
    handleRowFocus(nextIndex, withScrolling)
  })

  const focusedRowCells = useMemo(() => {
    return focusedID ? getRowCells?.(items[focusedRow]) ?? [] : []
  }, [focusedID, getRowCells])

  const { focusedCell, previousCell, nextCell } = useMemo(() => {
    if (focusedColIndex === -1) {
      return {
        focusedCell: null,
        previousCell: null,
        nextCell: focusedRowCells[0] ?? null
      }
    }
    for (let i = 0; i < focusedRowCells.length; i += 1) {
      const cell = focusedRowCells[i]
      if (
        focusedColIndex === cell.colIndex ||
        focusedColIndex < cell.colIndex + ((cell.colSpan ?? 1) - 1)
      ) {
        return {
          focusedCell: cell,
          previousCell: focusedRowCells[i - 1] ?? null,
          nextCell: focusedRowCells[i + 1] ?? null
        }
      }
    }
    return {
      focusedCell: last(focusedRowCells) ?? null,
      previousCell: focusedRowCells[focusedRowCells.length - 2] ?? null,
      nextCell: null
    }
  }, [focusedRowCells, focusedColIndex])

  const focusPreviousCell = useEvent(() => {
    setFocusedColIndex(previousCell?.colIndex ?? -1)
  })

  const focusNextCell = useEvent(() => {
    setFocusedColIndex(
      nextCell?.colIndex ?? last(focusedRowCells)?.colIndex ?? -1
    )
  })

  const isFocusedItemCollapsed = () => {
    if (!focusedID || !isItemCollapsed) return false
    const focusedItem = items.find(item => item.id === focusedID)
    return focusedItem ? isItemCollapsed(focusedItem) : false
  }

  const collapseFocusedElement = useEvent(() => {
    if (focusedID && !isFocusedItemCollapsed()) {
      onItemCollapse?.(focusedID)
    }
  })

  const expandFocusedElement = useEvent(() => {
    if (focusedID && isFocusedItemCollapsed()) {
      onItemCollapse?.(focusedID)
    }
  })

  const handleKeys = useEvent((event: React.KeyboardEvent) => {
    if (isUnmodifiedKeyPress(event)) {
      if (event.key === ArrowKeys.UP) {
        event.preventDefault()
        focusPreviousRow(true)
      }
      if (event.key === ArrowKeys.DOWN) {
        event.preventDefault()
        focusNextRow(true)
      }
      if (rowFocusOnly && event.key === ArrowKeys.LEFT) {
        if (!focusedID || !items[focusedRow]) {
          return
        }
        event.preventDefault()
        if (focusedID && !isFocusedItemCollapsed()) {
          collapseFocusedElement()
        } else {
          const immediateParentID = last(items[focusedRow].parents)
          if (immediateParentID) {
            setFocusedID(immediateParentID)
          }
        }
        return
      }
      if (rowFocusOnly && event.key === ArrowKeys.RIGHT) {
        event.preventDefault()
        if (focusedID && isFocusedItemCollapsed()) {
          expandFocusedElement()
        }
        return
      }
      // Enter is handled by the row element's own key handler
      // (useTableItem), which toggles selection once. Only Space is handled
      // here, so pressing Enter does not toggle twice (row + container).
      if (rowFocusOnly && focusedID && event.key === SPACE_KEY.key) {
        event.preventDefault()
        selectItem?.(focusedID, focusedRow)
        return
      }
      if (event.key === ArrowKeys.LEFT) {
        event.preventDefault()
        if (focusedColIndex === -1) {
          const isCollapsable = (items[focusedRow]?.childIDs?.length ?? 0) > 0
          if (focusedID && isCollapsable && !isFocusedItemCollapsed()) {
            collapseFocusedElement()
          } else {
            const immediateParentID = last(items[focusedRow].parents)
            if (immediateParentID) {
              setFocusedID(immediateParentID)
            }
          }
        }
        focusPreviousCell()
      }
      if (event.key === ArrowKeys.RIGHT) {
        event.preventDefault()
        if (focusedColIndex === -1 && focusedID && isFocusedItemCollapsed()) {
          expandFocusedElement()
        } else if (nextCell) {
          focusNextCell()
        }
      }
      if (event.key === HOME_KEY.key) {
        event.preventDefault()
        handleRowFocus(0, true)
      }
      if (event.key === END_KEY.key) {
        event.preventDefault()
        handleRowFocus(items.length - 1, true)
      }
      if (event.key === PAGE_UP_KEY.key) {
        event.preventDefault()
        handleRowFocus(Math.min(focusedRow + 10, items.length - 1), true)
      }
      if (event.key === PAGE_DOWN_KEY.key) {
        event.preventDefault()
        handleRowFocus(Math.max(focusedRow - 10, 0), true)
      }
    }
    if (isCommandKeyPress(event)) {
      if (event.key === A_KEY.key) {
        event.preventDefault()
        selectItems?.(items.map(item => item.id))
      }
    }
    if (isShiftKeyPress(event)) {
      if (event.key === SPACE_KEY.key) {
        event.preventDefault()
        if (focusedID) {
          selectItem?.(focusedID, focusedRow)
        }
      }
    }
  })

  useEffect(() => {
    if (isTableFocused) {
      focusableElement?.focus({ preventScroll: true })
    }
    if (!focusableElement && isTableFocused) {
      onFocusTable()
    }
  }, [focusableElement])

  return {
    isTableFocused,
    focusableElement,
    focusedRow,
    focusedCell,
    focusedColIndex,
    focusedID,
    onRowFocus: handleRowFocus,
    onKeyDown: handleKeys,
    onFocus: noop,
    onBlur: noop,
    setFocusableElement,
    setFocusedID
  }
}
